import React, {useEffect, useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Modal} from "../../../../components/modal/modal.jsx";
import Button from "../../../../components/button/button.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import InputTextarea from "../../../../components/input/input-textarea.jsx";
import Loader from "../../../../components/template/loader.jsx";
import DispatchService from "../../../../services/dispatch/dispatch-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import {formatCaseCard} from "../utils/dashboard-formatters.js";
import {DASHBOARD_QUERY_KEYS} from "../utils/dashboard-query-keys.js";
import {useDashboardContext} from "../context/dashboard-context.js";
import useDashboardModalState from "../hooks/use-dashboard-modal-state.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";

const DESPACHO_WEB_URL = import.meta.env.VITE_API_DESPACHO_WEB;

const getLastDisplacementAddress = dispatch => {
    const displacement = dispatch?.displacement?.[dispatch.displacement.length - 1];
    return [
        displacement?.destiny,
        displacement?.destinyNeighborhood,
        displacement?.destinyStreet,
        displacement?.destinyNumber,
    ].filter(Boolean).join(" - ");
};

const StartSupportModal = ({payload, onClose, onSuccess}) => {
    const navigate = navigateHook();
    const {configuration} = useDashboardContext();
    const [mileage, setMileage] = useState("");
    const [finalObservation, setFinalObservation] = useState("");
    const [mileageWarning, setMileageWarning] = useState(null);
    const [saveInvalidMileage, setSaveInvalidMileage] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const requested = Boolean(payload);
    const garrisonId = payload?.garrisonId;
    const [selectedSupportForGarrisonId, setSelectedSupportForGarrisonId] = useState("");
    const supportForGarrisonId = payload?.supportForGarrisonId ?? selectedSupportForGarrisonId;
    const requiresSupportSelection = requested && !payload?.supportForGarrisonId;

    const supportQuery = useQuery({
        queryKey: requiresSupportSelection
            ? DASHBOARD_QUERY_KEYS.garrisonBySetDispatchSupports(garrisonId)
            : DASHBOARD_QUERY_KEYS.garrisonBySetDispatch(garrisonId, supportForGarrisonId),
        enabled: requested && Boolean(garrisonId) && Boolean(supportForGarrisonId),
        queryFn: () => requiresSupportSelection
            ? DispatchService.getGarrisonBySetDispatchSupports(garrisonId)
            : DispatchService.getGarrisonBySetDispatch(garrisonId, supportForGarrisonId),
    });

    const supportListQuery = useQuery({
        queryKey: DASHBOARD_QUERY_KEYS.garrisonBySetDispatchSupports(garrisonId),
        enabled: requested && Boolean(garrisonId) && requiresSupportSelection,
        queryFn: () => DispatchService.getGarrisonBySetDispatchSupports(garrisonId),
    });

    const initialQuery = requiresSupportSelection ? supportListQuery : supportQuery;
    const isReady = !initialQuery.isFetching && (initialQuery.isSuccess || initialQuery.isError);
    const {open, closeModal, isPreparing} = useDashboardModalState({payload, isReady, onClose});

    useEffect(() => {
        if (!requested) return;

        setMileage("");
        setFinalObservation("");
        setSelectedSupportForGarrisonId("");
        setMileageWarning(null);
        setSaveInvalidMileage(false);
        setIsSubmitting(false);
    }, [requested, garrisonId, payload?.supportForGarrisonId]);

    useEffect(() => {
        if (!requested || (garrisonId && (supportForGarrisonId || requiresSupportSelection))) return;

        projectUtils.swalErrorMessage("Erro", "Não foi possível identificar as guarnições do apoio.", "Ok");
        closeModal();
    }, [closeModal, garrisonId, requested, requiresSupportSelection, supportForGarrisonId]);

    useEffect(() => {
        const result = supportQuery.data;
        const supportGarrison = result?.garrison;
        const dispatchToClose = result?.dispatch;

        if (!requested || !supportQuery.isSuccess) return;

        if (((!supportGarrison || !supportGarrison.status) && (!dispatchToClose || dispatchToClose.id === undefined))) {
            projectUtils.swalErrorMessage("Erro", "A guarnição não está disponível para despacho.", "Ok");
            closeModal();
            return;
        }

        if (configuration.isSuggestKm && supportGarrison?.kmControl && supportGarrison.actualKm !== undefined && supportGarrison.actualKm !== null) {
            setMileage(String(supportGarrison.actualKm));
        }
    }, [closeModal, configuration.isSuggestKm, requested, supportQuery.data, supportQuery.isSuccess]);

    const result = (requiresSupportSelection ? supportListQuery.data : supportQuery.data) ?? {};
    const supportGarrison = result.garrison;
    const dispatchToClose = result.dispatch;
    const dispatchForSupport = result.dispatchForSupport;
    const kmControl = Boolean(supportGarrison?.kmControl);
    const isLoading = supportQuery.isLoading || supportListQuery.isLoading;
    const hasError = supportQuery.isError || supportListQuery.isError;
    const supportOptions = useMemo(() => (result.listGarrisons ?? []).map(item => ({
        value: item.id,
        label: item.name,
    })), [result.listGarrisons]);

    const supportNature = useMemo(() => {
        const nature = dispatchForSupport?.nature ?? "";
        const option = (result.natures ?? []).find(item => (item.name ?? item.alias) === nature);
        return option?.alias ?? option?.name ?? nature;
    }, [dispatchForSupport?.nature, result.natures]);

    const supportAddress = useMemo(() => getLastDisplacementAddress(dispatchForSupport), [dispatchForSupport]);
    const dispatchToCloseAddress = useMemo(() => getLastDisplacementAddress(dispatchToClose), [dispatchToClose]);

    const handleMileageChange = ({target}) => {
        setMileage(target.value);
        setMileageWarning(null);
        setSaveInvalidMileage(false);
    };

    const validateMileage = async () => {
        if (!kmControl || mileage === "") return true;

        const mileageResult = await DispatchService.checkMileage(garrisonId, mileage);
        if (!mileageResult?.invalid || saveInvalidMileage) return true;

        setMileageWarning({
            actualKm: mileageResult.actualKm === "" || mileageResult.actualKm === null || mileageResult.actualKm === undefined ? "0" : mileageResult.actualKm,
        });
        return false;
    };

    const handleSubmit = async () => {
        if (!supportForGarrisonId) {
            projectUtils.swalErrorMessage("", "Nenhuma guarnição selecionada.", "Ok");
            return;
        }

        setIsSubmitting(true);

        try {
            const mileageIsValid = await validateMileage();
            if (!mileageIsValid) {
                setIsSubmitting(false);
                return;
            }

            const response = await DispatchService.setDispatchBySupport({
                garrisonId,
                mileage,
                garrisonSupportId: supportForGarrisonId,
                dispatchToCloseId: dispatchToClose?.id ?? null,
                finalObservation,
            });

            if (!response) {
                projectUtils.swalErrorMessage("Erro", "Não foi possível gerar o apoio.", "Ok");
                setIsSubmitting(false);
                return;
            }

            const checkbook = response.numberCheckbook ? ` Talão ${response.numberCheckbook}.` : "";
            onSuccess();
            projectUtils.swalSuccessMessage(
                "success",
                "Sucesso",
                `Apoio gerado com sucesso.${checkbook}`,
                "Ok",
                true,
                "Editar",
                ({isDismissed}) => {
                    if (isDismissed) {
                        navigate(`${DESPACHO_WEB_URL}/dispatch/form/${response.id}?redir=despacho/dashboard`, true);
                    }
                },
                false,
                false,
                "#fff",
                {cancelButton: "swal-edit-button"},
            );
        } catch (error) {
            console.error("Erro ao gerar apoio pelo dashboard:", error);
            const message = error.response?.data ?? "Não foi possível gerar o apoio.";
            projectUtils.swalErrorMessage("Erro", message, "Ok");
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {isPreparing && <Loader/>}
            <Modal open={open} setOpen={closeModal} title="Gerar despacho de apoio" className="w-full max-w-2xl" isCloseClick={false} animated={false}>
            <Modal.Body className="relative flex flex-col gap-4">
                {open && isLoading && <Loader/>}

                {hasError && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        Não foi possível carregar os dados para gerar o apoio.
                    </div>
                )}

                {!hasError && (
                    <>
                        <div>
                            <p className="text-[14px] font-bold text-[#676a6c] mb-0!">Guarnição de apoio</p>
                            <span className="text-sm text-[#555]">{supportGarrison?.name ?? payload?.garrison?.garrisonName}</span>
                        </div>

                        {requiresSupportSelection ? (
                            <SelectComponent
                                classNameWrapper="w-full"
                                label="Guarnição apoiada *"
                                value={selectedSupportForGarrisonId}
                                options={supportOptions}
                                onChange={(selected) => setSelectedSupportForGarrisonId(selected?.value ?? "")}
                            />
                        ) : (
                        <div className="flex flex-col">
                            <p className="text-[14px] font-bold text-[#676a6c] mb-0!">Dados do despacho apoiado</p>
                            <span className="text-sm text-[#555]">{supportNature}</span>
                            {supportAddress && <span className="text-sm text-[#555]">{formatCaseCard(supportAddress)}</span>}
                            <span className="text-sm text-[#555]">{dispatchForSupport?.garrison?.name ?? payload?.supportGarrison?.garrisonName ?? ""}</span>
                        </div>
                        )}

                        {kmControl && (
                            <InputForm
                                id="dashboard-support-mileage"
                                label="Km partida"
                                type="number"
                                value={mileage}
                                min={1}
                                onChange={handleMileageChange}
                                integerOnly
                            />
                        )}

                        {dispatchToClose && (
                            <>
                                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                                    <b>O despacho atual será encerrado.</b>
                                    <div>{dispatchToClose.nature}</div>
                                    {dispatchToCloseAddress && <div>{formatCaseCard(dispatchToCloseAddress)}</div>}
                                </div>

                                <InputTextarea label="Observação final" value={finalObservation}
                                               onChange={({target}) => setFinalObservation(target.value)} height={120}/>
                            </>
                        )}

                        {mileageWarning && (
                            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                <div className="mb-2 text-center">
                                    O km informado é menor que o atual. Deseja continuar?<br/>
                                    Km atual: <b>{mileageWarning.actualKm}</b>
                                </div>
                                <InputCheckbox
                                    id="dashboard-support-save-invalid-mileage"
                                    label="Sim, continuar mesmo assim"
                                    checked={saveInvalidMileage}
                                    onChange={({target}) => setSaveInvalidMileage(target.checked)}
                                />
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button type="button" target="primary" onClick={handleSubmit} loading={isSubmitting} disabled={hasError || isLoading} actionFeedbackType='save'>

                    Salvar
                </Button>
            </Modal.Footer>
            </Modal>
        </>
    );
};

export default StartSupportModal;